/**
 * Componentes compartilhados de apresentação do M7 (Consulta).
 * MarcadorPill mapeia o marcador de qualidade (ou o nível de um achado) para uma pill
 * de cor; NomeEscola mostra o nome como lido no termo com o oficial junto (ADR-035);
 * Pager é a navegação de páginas. A paginação segue o contrato por cursor (o número da
 * página vira o `cursor` enviado à API); aqui o Pager só exibe/avança o número.
 */

import { NivelAchado } from '../services/consulta-types';

const PILL_CLASSE: Record<NivelAchado, string> = {
  OK: 'pill pill-ok',
  CORRIGIDO: 'pill pill-corr',
  ALERTA: 'pill pill-dup',
  ERRO: 'pill pill-err',
  // ADR-035: observação é cinza — nunca a cor de alerta.
  OBSERVACAO: 'pill pill-obs',
};

const PILL_ROTULO: Partial<Record<NivelAchado, string>> = { OBSERVACAO: 'OBSERVAÇÃO' };

export function MarcadorPill({ marcador }: { marcador: NivelAchado | null }): JSX.Element {
  if (!marcador) {
    return <span className="pill pill-muted">—</span>;
  }
  return <span className={PILL_CLASSE[marcador]}>{PILL_ROTULO[marcador] ?? marcador}</span>;
}

/** Nome para comparar: sem caixa, sem acentos e sem espaços repetidos ou nas pontas. */
function chaveNome(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Dois nomes "diferem" quando ambos existem e não são o mesmo nome (ver `chaveNome`). */
export function nomesDiferem(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && chaveNome(a) !== chaveNome(b);
}

/**
 * Nome da escola de uma visita (ADR-035): o nome como lido no termo em cima e o oficial do
 * INEP embaixo, menor e em cinza, quando difere. Sem oficial (escola fora do catálogo), o
 * nome lido — ou, sem ele, `reserva` (o INEP lido) — com a etiqueta "fora do catálogo".
 * Sem nada, "—".
 */
export function NomeEscola({
  termo,
  oficial,
  reserva,
}: {
  readonly termo: string | null | undefined;
  readonly oficial: string | null | undefined;
  readonly reserva?: string | null;
}): JSX.Element {
  const principal = termo || oficial || reserva;
  if (!principal) {
    return <span className="muted">—</span>;
  }
  return (
    <span className="nome-escola">
      <span className="nome-escola-linha">
        <span className={termo ? 'nome-termo' : principal === reserva ? 'mono' : undefined}>{principal}</span>
        {!oficial && <span className="pill pill-muted">fora do catálogo</span>}
      </span>
      {nomesDiferem(termo, oficial) && <span className="nome-secundario">{oficial}</span>}
    </span>
  );
}

export function Pager({
  page,
  temProxima,
  onMudar,
}: {
  page: number;
  temProxima: boolean;
  onMudar: (page: number) => void;
}): JSX.Element {
  return (
    <div className="pager">
      <button
        className="btn btn-ghost"
        disabled={page <= 1}
        onClick={() => onMudar(page - 1)}
      >
        Anterior
      </button>
      <span className="muted">Página {page}</span>
      <button className="btn btn-ghost" disabled={!temProxima} onClick={() => onMudar(page + 1)}>
        Próxima
      </button>
    </div>
  );
}
